import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {InfoService} from "../services/info.service";

/**
 * Component for showing an image togehtner with some marker on the image.
 * The image is rendered as canvas object and can be greyscaled on a very basic base.
 * Optional a bigger version of the image can be shown in an overlay.
 */
@Component({
    selector: 'app-canvas-image',
    templateUrl: './canvas-image.component.html',
    standalone: false
})
export class CanvasImageComponent implements OnInit, OnChanges {
  /**
   * web-path for the image.
   */
  @Input() image: string

  /**
   * Should the output converted to a greyscaled image-
   */
  @Input() greyscale: boolean = false;

  /**
   * Crop the image on some coordinates.
   *
   * @example x1,y1,x1,y2
   * @example '12,130,5,500'
   */
  @Input() crop: string = null;

  /**
   * Optional, crop the image by a marker coordinates (top, left, width, height)
   */
  @Input() cropByMarker: any = null;

  /**
   * extra vertical padding for the crop.
   */
  @Input() cropPaddingVertical: number = 0;

  /**
   * extra horizontal padding for the crop.
   */
  @Input() cropPaddingHorizontal: number = 0;

  /**
   * marker that should be placed on the image.
   */
  @Input() marker: {top: number, left: number, width: number, height: number}[] = null;

  /**
   * Is the overlay activated or not.
   */
  @Input() hasOverlay: boolean = false;

  /**
   * Eventemitter if zthe markers are set
   */
  @Output() markerSet: EventEmitter<{top: number, left: number, bottom: number, right: number}> = new EventEmitter<{top: number, left: number, bottom: number, right: number}>();

  /**
   * The canvas element for the (cropped) image
   */
  @ViewChild('canvasElement') canvasElement: ElementRef<HTMLCanvasElement>;

  /**
   * toggle for showing/hiding the overlay.
   */
  showOverlay: boolean = false;

  /**
   * constructor.
   */
  constructor() { }

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    this.initImage();
  }

  /**
   * Initialize the image wich we maybe have. Ask the backend for image data.
   * If there is none, nothing is shown.
   */
  initImage(): void {
    // show image as cropped canvas element
    const image = new Image();
    image.onload = () => {
      if(this.cropByMarker != null) {
        // calculate crop coordinates aout of a marker
        this.crop = this.cropByMarker[0]['left'] + ',' + this.cropByMarker[0]['top'] + ',' +
          parseFloat(this.cropByMarker[0]['left'] + this.cropByMarker[0]['width']) + ',' + parseFloat(this.cropByMarker[0]['top'] + this.cropByMarker[0]['height']);
      }

      // get the coordinates for cropping, x1,y1,x2,y2
      const coordinates = this.crop? this.crop.split(",").map(element => parseInt(element)) : [0, 0, image.width, image.height];

      if(this.cropPaddingHorizontal) {
        coordinates[0] = coordinates[0] - this.cropPaddingHorizontal;
        coordinates[2] = coordinates[2] + this.cropPaddingHorizontal;
      }
      if(this.cropPaddingVertical) {
        coordinates[1] = coordinates[1] - this.cropPaddingVertical;
        coordinates[3] = coordinates[3] + this.cropPaddingVertical;
      }

      const sourceX = coordinates[0];
      const sourceY = coordinates[1];
      const sourceWidth = coordinates[2] - coordinates[0];
      const sourceHeight = coordinates[3] - coordinates[1];
      const destWidth = sourceWidth;
      const destHeight = sourceHeight;
      this.canvasElement.nativeElement.width = destWidth;
      this.canvasElement.nativeElement.height = destHeight;

      // draw the cropped image
      const context = this.canvasElement.nativeElement.getContext("2d");
      context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, destWidth, destHeight);

      if(this.greyscale === true) {
        // convert the image to greyscale version
        const imgData = context.getImageData(0, 0, destWidth, destHeight);
        for (let i = 0; i < imgData.data.length; i += 4) {
          const count = imgData.data[i] + imgData.data[i + 1] + imgData.data[i + 2];
          let colour = 0;
          if (count > 510) colour = 255;
          else if (count > 255) colour = 127.5;

          imgData.data[i] = colour;
          imgData.data[i + 1] = colour;
          imgData.data[i + 2] = colour;
          imgData.data[i + 3] = 255;
        }
        context.putImageData(imgData, 0, 0);
      }

      // draw marker
      if(this.marker) {
        context.fillStyle = InfoService.INFOBUTTON_MARKER_FILL;
        context.strokeStyle = InfoService.INFOBUTTON_MARKER_BORDER;
        for(const marker of this.marker) {
          context.fillRect(marker.left - sourceX,marker.top - sourceY, marker.width, marker.height);
          context.strokeRect(marker.left - sourceX,marker.top - sourceY, marker.width, marker.height);

          // send position of the marker to the emitter. Position is calculated as percentage value from top/left.
          this.markerSet.emit({
            left: (marker.left - sourceX) / destWidth,
            top: (marker.top - sourceY) / destHeight,
            bottom: (destHeight - (marker.top - sourceY + marker.height)) / destHeight,
            right: (destWidth - (marker.left - sourceX + marker.width)) / destWidth
          });
        }
      }
    };
    image.src = this.image;
  }

  /**
   * Show/hide the overlay.
   */
  toggleOverlay(): void {
    if(this.hasOverlay === false) {
      // do nothing, we do not want an overlay
      return;
    }
    this.showOverlay = !this.showOverlay;
  }

  /**
   * when changes on the image or marker occure, update the element.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if((changes.hasOwnProperty('image') && changes['image'].firstChange === false) ||
      (changes.hasOwnProperty('crop') && changes['crop'].firstChange === false) ||
      (changes.hasOwnProperty('cropByMarker') && changes['cropByMarker'].firstChange === false) ||
      (changes.hasOwnProperty('marker') && changes['marker'].firstChange === false)) {
      this.initImage();
    }
  }

}
